import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Box, Typography } from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

// Import all card components
import ProjectNameCard from './cards/ProjectNameCard';
import PipeCoverageCard from './cards/PipeCoverageCard';
import SensorsInstalledCard from './cards/SensorsInstalledCard';
import WaterSavedCard from './cards/WaterSavedCard';
import LeaksFoundCard from './cards/LeaksFoundCard';
import FoundLeaksSummaryCard from './cards/FoundLeaksSummaryCard';
import CurrentStatusCard from './cards/CurrentStatusCard';
import WorkflowTrendCard from './cards/WorkflowTrendCard';
import NotificationSection from './cards/NotificationSection';
import MaintenanceViewCard from './cards/MaintenanceViewCard';
import AverageTimeCard from './cards/AverageTimeCard';
import { BLUE_VACATION_LIGHT } from '../../dashboardColors';
import { buildDashboardEnumOptions, capitalizeText } from '../../../../utils/dashboardEnums';

import './ProjectView.scss';

const ProjectView = ({
  project,
  onAlertClick,
  onFiltersChange,
  sharedPriorityFilter,
  sharedEventStateFilter,
  filtersInitialized,
  // Redux props
  dashboardStats,
  weeklyStats,
  reports,
  operationalAlerts,
  alertsHistoryQuarterly,
  alertsLifecycleQuarterly,
  alertsYearlyTrend,
  options,
  fetchDashboardStats,
  fetchDashboardWeeklyStats,
  fetchDashboardReport,
  fetchLeaksIfNeeded,
  fetchOperationalAlerts,
  fetchAlertsHistoryQuarterly,
  fetchAlertsLifecycleQuarterly,
  fetchAlertsYearlyTrend,
  updateOperationalAlert,
  operationalAlertsSettings,
  fetchOperationalAlertsSettings,
  updateOperationalAlertsSettings,
  updateDashboardProjectName,
  updateProjectPipeCoverage,
  customerLogo,
  fetchCustomerLogo,
  deviceTypesStatsWeekly,
  fetchDeviceTypesStatsWeekly,
  units,
  unitFactor,
  user,
  notify
}, context) => {

  const getOptionLabel = (optionType, value) => {
    if (!options || !options[optionType] || value == null) return null;

    // Check if this is a dashboard enum with reversed fields
    const isDashboardEnum = optionType.startsWith('dashboard_enum_');

    const option = options[optionType].find(opt => {
      if (isDashboardEnum) {
        // For dashboard enums, 'label' contains the numeric value
        return opt.label == value;
      } else {
        // For other options like AlertState, try standard mapping
        return opt.value == value || opt.code == value;
      }
    });

    if (option) {
      let result;
      if (isDashboardEnum) {
        // For dashboard enums, return the 'value' field which contains the text
        result = option.value;
      } else {
        // For other options, use DisplayName (translated from DB)
        result = option.DisplayName;
      }

      // Capitalize the result for priority and event state
      if (optionType === 'dashboard_enum_priority' || optionType === 'dashboard_enum_opr_state') {
        return capitalizeText(result);
      }

      return result;
    }

    return null;
  };

  // Fetch dashboard data when component mounts or project changes
  useEffect(() => {
    if (project?.ID) {
      fetchDashboardStats(project.ID);
      fetchDashboardWeeklyStats(project.ID, 12);
      fetchOperationalAlertsSettings(project.ID);
      fetchLeaksIfNeeded(project.ID);
      fetchOperationalAlerts(project.ID);
      fetchAlertsHistoryQuarterly(project.ID);
      fetchAlertsLifecycleQuarterly(project.ID);
      fetchAlertsYearlyTrend(project.ID);
      fetchDeviceTypesStatsWeekly(project.ID);

      if (project.CustomerID) {
        fetchCustomerLogo(project.CustomerID);
      }
    }
  }, [project?.ID, project?.CustomerID, fetchDashboardStats, fetchDashboardWeeklyStats, fetchDashboardReport, fetchLeaksIfNeeded, fetchOperationalAlerts, fetchAlertsHistoryQuarterly, fetchAlertsLifecycleQuarterly, fetchAlertsYearlyTrend, fetchDeviceTypesStatsWeekly, fetchCustomerLogo]);

  // Get operational alerts loading state outside of useMemo
  const operationalAlertsLoading = operationalAlerts?.loading || false;
  
  // Process data from Redux store
  const processedData = React.useMemo(() => {
    const stats = dashboardStats?.data || {};
    const operationalAlertsArray = Array.isArray(operationalAlerts?.data) ? operationalAlerts.data : [];

    // Handle both array and object responses for stats
    const statsData = Array.isArray(stats) ? stats[0] : stats;

    // Get the latest week's data from weeklyStats for water saved values
    const weeklyData = weeklyStats?.data || [];
    const latestWeekData = weeklyData.length > 0 ? weeklyData[0] : {};

    return {
      pipeLength: {
        total: statsData?.pipes_km || 0,
        covered: statsData?.pipes_covered_km || 0
      },
      sensors: {
        total: statsData?.num_sensors || 0,
        active: statsData?.num_active_sensors || 0,
        inactive: statsData?.num_inactive_sensors || 0
      },
      leaks: {
        total: (statsData?.num_fixed_alerts || 0) +
               (statsData?.num_is_repaired_alerts || 0) +
               (statsData?.num_located_alerts || 0) +
               (statsData?.num_tbf_alerts || 0),
        yearly: alertsYearlyTrend?.data || [], // Using data from new API endpoint
        fixed: statsData?.num_fixed_alerts || 0 // Pass fixed count directly from stats
      },
      currentStatus: {
        toBeLocated: statsData?.num_tbl_alerts || 0,
        located: statsData?.num_located_alerts || 0,
        toBeFixed: statsData?.num_tbf_alerts || 0,
        isRepaired: statsData?.num_is_repaired_alerts || 0,
        fixed: statsData?.num_fixed_alerts || 0
      },
      notifications: (() => {
        if (operationalAlertsLoading || !operationalAlertsArray.length) {
          return [];
        }

        const processedNotifications = operationalAlertsArray.map((opAlert) => {
          // Use the clean field names from operational alerts
          const alertId = opAlert.alert_id;

          const priorityValue = opAlert.event_priority;
          const priority = getOptionLabel('dashboard_enum_priority', priorityValue) || 'High';
          const alertStatusValue = opAlert.alert_state;

          // Use English strings for indicator to match getIndicatorIcon switch cases
          let indicator = null;
          if (alertStatusValue !== 5) { // Not closed
            if (opAlert.is_burst === 1) {
              indicator = 'Burst';
            } else if (opAlert.LeakGrowthTrend) {
              const trend = opAlert.LeakGrowthTrend.toLowerCase();
              if (trend === 'up') {
                indicator = 'Growing Up';
              } else if (trend === 'down') {
                indicator = 'Growing Down';
              }
            }
          }

          // Check is_over field for "No Event" indicator (overrides previous indicators)
          if (project?.is_display_end_event) {
            if (opAlert.is_over
              && (opAlert.event_type == 0 || opAlert.event_type == 3) // Leak or Private Leak
              && [5, 6].indexOf(alertStatusValue) === -1 // statuses: [Closed, Fixed]
            ) {
              indicator = 'No Event';
            }
          }
          // Get AlertState text from options (uses DisplayName for translation)
          const alertStatus = getOptionLabel('AlertState', alertStatusValue) || context.t('unknown');
          const probability = parseFloat(opAlert.alert_probability || 0);

          const openTypeMap = {0: context.t('auto'), 1: context.t('manual'), 2: context.t('import')};
          const openType = getOptionLabel('EventOpenType', opAlert.event_open_type) ||
                          openTypeMap[opAlert.event_open_type] || context.t('auto');

          const eventStateValue = opAlert.event_state;
          const eventState = getOptionLabel('dashboard_enum_opr_state', eventStateValue) || '';
          const comment = opAlert.alert_comment || '';

          return {
            id: alertId,
            type: 'standard',
            alertId,
            priority,
            priorityValue: priorityValue != null ? parseInt(priorityValue) : undefined,
            indicator,
            alertStatus: String(alertStatus),
            probability,
            openType,
            eventState,
            eventStateValue: eventStateValue != null ? parseInt(eventStateValue) : 0,
            comment,
            location: '',
            daysOpen: 0,
            autoclose: opAlert.autoclose,
            eventType: opAlert.event_type,
            avgDailyGrowth: opAlert.AvgDailyGrowth,
            avgGrowthAngle: opAlert.AvgGrowthAngle
          };
        });

        return processedNotifications;
      })(),
      redSensors: statsData?.num_inactive_sensors || 0,
      totalSensors: statsData?.num_sensors || 0,
      waterSavedActual: latestWeekData?.water_saved_lt_ttl_actual || 0,
      waterSavedPotential: latestWeekData?.water_saved_lt_ttl || 0,
      trends: {
        locationTime: (() => {
          const lifecycleData = alertsLifecycleQuarterly?.data || [];
          return lifecycleData.map(q => ({
            quarter: `${q.year} Q${q.quarter}`,
            value: Math.round(q.avg_locating || 0)
          })).slice(-4);
        })(),
        repairTime: (() => {
          const lifecycleData = alertsLifecycleQuarterly?.data || [];
          return lifecycleData.map(q => ({
            quarter: `${q.year} Q${q.quarter}`,
            value: Math.round(q.avg_repair || 0)
          })).slice(-4);
        })()
      },
      workflowTrend: (() => {
        // Use quarterly alerts history data from the new endpoint
        const quarterlyData = alertsHistoryQuarterly?.data || [];

        // If we have data, use the actual year and quarter from the server
        if (quarterlyData.length > 0) {
          // Sort first by year and quarter, then take last 4 items
          const sortedData = [...quarterlyData].sort((a, b) => {
            if (a.year !== b.year) {
              return a.year - b.year;
            }
            return a.quarter - b.quarter;
          });

          // Take only the last 4 items from the sorted data
          const relevantData = sortedData.slice(-4);

          return relevantData.map(dataItem => ({
            quarter: `${dataItem.year} Q${dataItem.quarter}`,
            toBeLocated: dataItem.total_tbl_alerts || 0,
            located: dataItem.total_located_alerts || 0,
            toBeFixed: (dataItem.total_tbf_alerts || 0) + (dataItem.total_repaired_alerts || 0),
            fixed: dataItem.total_fixed_alerts || 0
          }));
        }
        return [];
      })()
    };
  }, [dashboardStats, weeklyStats, reports, operationalAlerts, operationalAlertsLoading, alertsHistoryQuarterly, alertsLifecycleQuarterly, options, project?.ID, context.t]);

  // Show loading state
  const isLoading = dashboardStats?.loading || weeklyStats?.loading ||
                   reports.alertReport?.loading || operationalAlerts?.loading;

  // Handle event state change
  const handleEventStateChange = (alertId, newState) => {
    updateOperationalAlert(alertId, { event_state: parseInt(newState) });
  };

  // Handle event priority change
  const handleEventPriorityChange = (alertId, newPriority) => {
    updateOperationalAlert(alertId, { event_priority: parseInt(newPriority) });
  };

  // Get event state options from server
  const eventStateOptions = React.useMemo(() => {
    return buildDashboardEnumOptions(options?.dashboard_enum_opr_state, ['is_opr_alerts', 'is_opr']);
  }, [options]);

  // Get event priority options from server
  const eventPriorityOptions = React.useMemo(() => {
    return buildDashboardEnumOptions(options?.dashboard_enum_priority);
  }, [options]);

  const isMinimalView = operationalAlertsSettings?.data?.is_minimal_view === 1;

  return (
    <Box className={`project-view-container ${isMinimalView ? 'minimal-view' : ''}`} sx={{ display: 'flex', flexDirection: 'column' }}>
      {isLoading && (
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000
        }}>
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {context.t('loading_data')}
          </Typography>
        </Box>
      )}
      <Grid container spacing={1.5}>
        {/* Row 1 - Top Cards: Project 15%, Pipe 17.5%, Sensors 17.5%, Leaks 25%, Water Saved 25% */}
        <Grid item xs={12} md={1.8}>
          <ProjectNameCard
            projectName={project?.dashboard_name || project?.ShortName}
            projectData={project}
            customerLogoUrl={customerLogo?.data}
            dashboardSettings={operationalAlertsSettings?.data}
            t={context.t}
            user={user}
            units={units}
            updateDashboardProjectName={updateDashboardProjectName}
            onUpdateSettings={(settings) => {
              if (project?.ID) {
                // Only send dashboard display settings fields
                updateOperationalAlertsSettings(project.ID, {
                  is_maintenance_view: settings.is_maintenance_view,
                  is_watersave_view: settings.is_watersave_view,
                  is_minimal_view: settings.is_minimal_view,
                  unit_is_usg: settings.unit_is_usg,
                  // dashboard_display_name: settings.dashboard_display_name,
                  // potential_pipe_coverage: settings.potential_pipe_coverage
                });
              }
            }}
            notify={notify}
          />
        </Grid>
        <Grid item xs={12} md={2.1}>
          <PipeCoverageCard
            t={context.t}
            units={units}
            user={user}
            data={processedData.pipeLength}
            projectData={project}
            unitFactor={unitFactor}
            updateProjectPipeCoverage={updateProjectPipeCoverage}
          />
        </Grid>
        <Grid item xs={12} md={2.1}>
          <SensorsInstalledCard data={processedData.sensors} t={context.t} />
        </Grid>
        <Grid item xs={12} md={3}>
          <FoundLeaksSummaryCard
            totalLeaks={processedData.leaks.total}
            fixedLeaks={processedData.leaks.fixed}
            t={context.t}
          />
        </Grid>
        {operationalAlertsSettings?.data?.is_watersave_view !== 0 && (
          <Grid item xs={12} md={3}>
            <WaterSavedCard
              waterSavedActual={processedData.waterSavedActual}
              waterSavedPotential={processedData.waterSavedPotential}
              t={context.t}
              units={units}
              unitIsUsg={operationalAlertsSettings?.data?.unit_is_usg}
            />
          </Grid>
        )}

        {/* Row 2 - Overview Section with better padding and alignment */}
        <Grid item xs={12} md={6}>
          <NotificationSection
            notifications={processedData.notifications}
            onAlertClick={onAlertClick}
            onEventStateChange={handleEventStateChange}
            onEventPriorityChange={handleEventPriorityChange}
            isLoading={operationalAlertsLoading}
            eventStateOptions={eventStateOptions}
            eventPriorityOptions={eventPriorityOptions}
            operationalAlertsSettings={operationalAlertsSettings?.data}
            onFiltersChange={onFiltersChange}
            sharedPriorityFilter={sharedPriorityFilter}
            sharedEventStateFilter={sharedEventStateFilter}
            filtersInitialized={filtersInitialized}
            onUpdateOperationalAlertsSettings={(settings) => {
              if (project?.ID) {
                updateOperationalAlertsSettings(project.ID, settings);
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <CurrentStatusCard
            data={processedData.currentStatus}
            t={context.t}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <WorkflowTrendCard
            data={processedData.workflowTrend}
            t={context.t}
          />
        </Grid>

        {/* Row 3 - Bottom section aligned with Row 1 columns */}
        {/* Only show when minimal view is NOT enabled */}
        {operationalAlertsSettings?.data?.is_minimal_view !== 1 && (
          <>
            {/* Leak Detection Time - aligns with ProjectNameCard (column 1) */}
            <Grid item xs={12} md={3}>
              <AverageTimeCard
                title={context.t('leak_detection_time_average')}
                icon={<HourglassEmptyIcon />}
                data={processedData.trends.locationTime}
                t={context.t}
              />
            </Grid>

            {/* Leak Repair Time - aligns with PipeCoverageCard (column 2) */}
            <Grid item xs={12} md={3}>
              <AverageTimeCard
                title={context.t('leak_repair_time_average')}
                icon={<HourglassEmptyIcon />}
                data={processedData.trends.repairTime}
                t={context.t}
                barColor={BLUE_VACATION_LIGHT}
              />
            </Grid>

            {/* Leaks Found - aligns with SensorsInstalledCard (column 3) */}
            <Grid item xs={12} md={3}>
              <LeaksFoundCard data={processedData.leaks} t={context.t} />
            </Grid>

            {/* Maintenance view - aligns with WaterSavedCard (column 4) */}
            {operationalAlertsSettings?.data?.is_maintenance_view !== 0 && (
              <Grid item xs={12} md={3}>
                <MaintenanceViewCard
                  redSensors={processedData.redSensors}
                  totalSensors={processedData.totalSensors}
                  deviceTypesData={deviceTypesStatsWeekly?.data || []}
                  t={context.t}
                />
              </Grid>
            )}
          </>
        )}
      </Grid>
    </Box>
  );
};

ProjectView.propTypes = {
  project: PropTypes.object,
  onAlertClick: PropTypes.func,
  onFiltersChange: PropTypes.func,
  sharedPriorityFilter: PropTypes.array,
  sharedEventStateFilter: PropTypes.array,
  filtersInitialized: PropTypes.bool,
  // Redux props
  dashboardStats: PropTypes.object,
  weeklyStats: PropTypes.object,
  reports: PropTypes.object,
  operationalAlerts: PropTypes.object,
  alertsHistoryQuarterly: PropTypes.object,
  alertsLifecycleQuarterly: PropTypes.object,
  selectedProject: PropTypes.string,
  options: PropTypes.object,
  fetchDashboardStats: PropTypes.func.isRequired,
  fetchDashboardWeeklyStats: PropTypes.func.isRequired,
  fetchDashboardReport: PropTypes.func.isRequired,
  fetchLeaksIfNeeded: PropTypes.func.isRequired,
  fetchOperationalAlerts: PropTypes.func.isRequired,
  fetchAlertsHistoryQuarterly: PropTypes.func.isRequired,
  fetchAlertsLifecycleQuarterly: PropTypes.func.isRequired,
  fetchAlertsYearlyTrend: PropTypes.func.isRequired,
  updateOperationalAlert: PropTypes.func.isRequired,
  operationalAlertsSettings: PropTypes.object,
  fetchOperationalAlertsSettings: PropTypes.func.isRequired,
  updateOperationalAlertsSettings: PropTypes.func.isRequired,
  customerLogo: PropTypes.object,
  fetchCustomerLogo: PropTypes.func.isRequired,
  deviceTypesStatsWeekly: PropTypes.object,
  fetchDeviceTypesStatsWeekly: PropTypes.func.isRequired,
  units: PropTypes.string,
  unitFactor: PropTypes.object,
  user: PropTypes.object
};

ProjectView.contextTypes = {
  t: PropTypes.func.isRequired
};

export default ProjectView;
